import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout } from '../DashboardLayout';
import { useAuthStore } from '../../store/authStore';
import glassTheme from '../../styles/glassTheme';
import { DataFigure, rate } from './AnalyticsFigures';
import { RecordNotesLayout, useRecordNotes } from './RecordNotes';
import type { ForegroundDuration } from '../../types/learningTiming';
import type { AnalyticsTopic, Cohort, Completion, CourseCard, Fraction, Measure, Meta, Page, Reason, Student, Success } from '../../types/teacherAnalytics';
export { rate } from './AnalyticsFigures';

export const Surface = styled.div`
  color: ${glassTheme.colors.text.primary}; min-width: 0; overflow-wrap: anywhere;
  * { box-sizing: border-box; min-width: 0; }
  a:not([data-dashboard-navigation] a) { color: ${glassTheme.colors.accent.cyan}; text-underline-offset: 3px; }
  a:not([data-dashboard-navigation] a):hover, a:not([data-dashboard-navigation] a):focus-visible { text-decoration-thickness: 2px; }
  :is(a, button, input, summary, [tabindex]):focus-visible {
    outline: 3px solid ${glassTheme.colors.accent.cyan}; outline-offset: 3px;
  }
  button:not([data-dashboard-navigation] button), input { color: ${glassTheme.colors.text.primary}; background: ${glassTheme.colors.background};
    border: 1px solid ${glassTheme.colors.border}; border-radius: ${glassTheme.borderRadius.md};
    font: inherit; padding: ${glassTheme.spacing.sm} ${glassTheme.spacing.md}; min-height: 44px; max-width: 100%; }
  button { cursor: pointer; }
  button:disabled { cursor: not-allowed; opacity: .5; }
  button:hover:not(:disabled) { border-color: ${glassTheme.colors.borderHover}; }
  nav, form { display: flex; flex-wrap: wrap; gap: ${glassTheme.spacing.md}; align-items: center; margin-bottom: ${glassTheme.spacing.lg}; }
  nav a { padding: ${glassTheme.spacing.sm}; }
  [aria-current=page] { border-bottom: 2px solid ${glassTheme.colors.accent.cyan}; font-weight: 700; }
  h2 { font-size: 1.25rem; } h3 { font-size: 1.1rem; }
  p, small { color: ${glassTheme.colors.text.secondary}; }
  small { display: block; margin-top: ${glassTheme.spacing.xs}; }
  details { margin-top: ${glassTheme.spacing.sm}; }
  summary { cursor: pointer; min-height: 44px; }
  @media (max-width: 768px) { form { align-items: stretch; } form input { width: 100%; } }
  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { transition: none !important; animation: none !important; } }
`;
export const Panel = styled.section`
  ${glassTheme.glass.container}
  padding: ${glassTheme.spacing.lg}; margin-bottom: ${glassTheme.spacing.xl}; min-width: 0;
  @media (max-width: 768px) { padding: ${glassTheme.spacing.md}; }
`;
export const Metrics = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr));
  gap: ${glassTheme.spacing.lg}; margin: ${glassTheme.spacing.lg} 0;
  > div { ${glassTheme.glass.light} padding: ${glassTheme.spacing.md}; }
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); }
`;
export const Records = styled.div`
  display: grid; gap: ${glassTheme.spacing.md};
  article { ${glassTheme.glass.light} padding: ${glassTheme.spacing.md}; }
  dl { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 170px), 1fr)); gap: ${glassTheme.spacing.md}; }
  dt { color: ${glassTheme.colors.text.secondary}; margin-bottom: ${glassTheme.spacing.sm}; }
  dd { margin: 0; }
  @media (max-width: 768px) { dl { grid-template-columns: minmax(0, 1fr); } }
`;
/** One semantic representation: columns on desktop, labelled row cards on mobile. */
export const ResponsiveTable = styled.table`
  width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0;
  margin: ${glassTheme.spacing.lg} 0;
  th, td { padding: ${glassTheme.spacing.md}; text-align: left; vertical-align: top;
    overflow-wrap: anywhere; border-bottom: 1px solid ${glassTheme.colors.border}; }
  th { color: ${glassTheme.colors.text.secondary}; font-size: .875rem; }
  @media (max-width: 768px) {
    &, tbody, tr, td { display: block; width: 100%; }
    thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
    tbody tr { ${glassTheme.glass.light} margin-bottom: ${glassTheme.spacing.md}; padding: ${glassTheme.spacing.sm}; }
    td { border: 0; padding: ${glassTheme.spacing.sm}; }
    td::before { content: attr(data-label); display: block; color: ${glassTheme.colors.text.secondary};
      font-weight: 600; margin-bottom: ${glassTheme.spacing.xs}; }
  }
`;
export const PlainText = styled.p`white-space: pre-wrap; overflow-wrap: anywhere;`;
const Fatal = createContext<(error: RequestError) => void>(() => {});
export interface RequestError { status: number; code: string }
export const errorCopy = (e: RequestError) => ({
  UNAUTHENTICATED: '登录已失效，请重新登录。',
  TEACHER_ANALYTICS_FORBIDDEN: '暂无权限查看学习记录。',
  ANALYTICS_SCOPE_NOT_FOUND: '记录不可用或已无权访问。',
  INVALID_ANALYTICS_REQUEST: '链接或查询条件无效，请返回列表重试。',
  ANALYTICS_QUERY_TIMEOUT: '加载超时，请重试。',
  ANALYTICS_SCOPE_TOO_LARGE: '数据量较大，暂时无法加载此面板。'
} as Record<string, string>)[e.code] || '加载失败，请重试。';
function Boundary({ children }: {children: React.ReactNode}) {
  const [fatal, setFatal] = useState<RequestError | null>(null);
  const logout = useAuthStore(s => s.logout);
  useEffect(() => { if (fatal?.status === 401) void logout(false); }, [fatal, logout]);
  if (fatal) return <Panel role="alert"><p>{errorCopy(fatal)}</p>
    <Link to={fatal.status === 401 ? '/login' : '/teacher/dashboard?view=students&page=1'}>
      {fatal.status === 401 ? '重新登录' : '返回教师工作台'}</Link></Panel>;
  return <Fatal.Provider value={setFatal}>{children}</Fatal.Provider>;
}
/** Remounts private state on identity, credential generation, route and query changes. */
export function AnalyticsGate({children}: {children: React.ReactNode}) {
  const { user, token } = useAuthStore();
  const location = useLocation();
  const approved = user?.teacherInfo?.approvalStatus === 'approved';
  const scope = JSON.stringify([user?.id, token, location.pathname, location.search]);
  if (!token) return <Surface><Panel role="alert">登录已失效，请重新登录。 <Link to="/login">重新登录</Link></Panel></Surface>;
  if (!approved) return <Surface><Panel role="status">
    {user?.teacherInfo?.approvalStatus === 'rejected' ? `申请已被拒绝：${user.teacherInfo.rejectionReason || '未说明'}` : '您的教师账号正在等待管理员审核批准，请耐心等待。'}
    <p><Link to="/teacher/dashboard">返回教师工作台</Link></p>
  </Panel></Surface>;
  const query = new URLSearchParams(location.search);
  const allowed = new Set(['page', 'view', 'tab', 'topicId', 'q', 'exercisePage', 'historyPage', 'explanationPage', 'explanationLimit', 'activityId', 'exerciseId']);
  const badQuery = Array.from(query.keys()).some(key => !allowed.has(key) || query.getAll(key).length !== 1) ||
    ['page', 'exercisePage', 'historyPage', 'explanationPage', 'explanationLimit'].some(key => {
      const value = query.get(key);
      return value !== null && (!/^[1-9]\d*$/.test(value) || Number(value) > (key === 'explanationLimit' ? 50 : 10000));
    }) || Array.from((query.get('q') || '').trim()).length > 80 ||
    (query.has('topicId') && !/^[a-fA-F0-9]{24}$/.test(query.get('topicId')!)) ||
    ['activityId', 'exerciseId'].some(key => query.has(key) && (!query.get(key) || Array.from(query.get(key)!).length > 256)) ||
    query.has('activityId') !== query.has('exerciseId');
  if (badQuery) return <Surface><Panel role="alert">链接或查询条件无效，请返回列表重试。
    <p><Link to="/teacher/dashboard?view=students&page=1">返回教师工作台</Link></p></Panel></Surface>;
  return <Surface><Boundary key={scope}>{children}</Boundary></Surface>;
}
export interface Resource<T> { result?: Success<T>; loading: boolean; error?: RequestError; retry: () => void }
export function useAnalytics<T>(key: string, fetcher: (signal: AbortSignal) => Promise<Success<T>>, enabled = true): Resource<T> {
  const token = useAuthStore(s => s.token);
  const identity = useAuthStore(s => s.user?.id);
  const scope = JSON.stringify([identity, token, key]);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{key: string; result?: Success<T>; loading: boolean; error?: RequestError}>({key: scope, loading: enabled});
  const generation = useRef(0);
  const loader = useRef(fetcher);
  loader.current = fetcher;
  const fatal = useContext(Fatal);
  useEffect(() => {
    const controller = new AbortController();
    const current = ++generation.current;
    if (!enabled || !token) return () => controller.abort();
    setState({key: scope, loading: true});
    loader.current(controller.signal).then(result => {
      if (!controller.signal.aborted && generation.current === current) setState({key: scope, loading: false, result});
    }).catch(e => {
      if (controller.signal.aborted || generation.current !== current) return;
      const status = e.response?.status || 0;
      const code = e.response?.data?.code || ({401: 'UNAUTHENTICATED', 403: 'TEACHER_ANALYTICS_FORBIDDEN', 404: 'ANALYTICS_SCOPE_NOT_FOUND'} as Record<number, string>)[status] || 'ANALYTICS_READ_FAILED';
      const error = {status, code};
      setState({key: scope, loading: false, error});
      if ([401, 403, 404].includes(status)) fatal(error);
    });
    return () => { controller.abort(); generation.current++; };
  }, [scope, enabled, revision, token, fatal]);
  const visible = state.key === scope && enabled && token ? state : {loading: enabled};
  return {...visible, retry: () => setRevision(r => r + 1)};
}
export function LoadState<T>({resource, children, label = '正在加载学习记录…'}:
  {resource: Resource<T>; children: (data: T, meta: Meta) => React.ReactNode; label?: string}) {
  if (resource.loading) return <p role="status" aria-live="polite">{label}</p>;
  if (resource.error) return <div role="alert"><p>{errorCopy(resource.error)}</p><button onClick={resource.retry}>重试</button></div>;
  if (!resource.result) return null;
  return <><span role="status" aria-live="polite">加载完成</span><Warnings meta={resource.result.meta} />
    {children(resource.result.data, resource.result.meta)}</>;
}
export function Warnings({meta}: {meta: Meta}) {
  const labels: Record<string, string> = {
    invalid_roster_id: '注册标识无效', missing_user: '学生账号缺失', inactive_enrollment: '注册已失效',
    conflicting_enrollment: '注册状态冲突', missing_profile_enrollment: '注册资料缺失',
    invalid_enrollment_date: '注册时间不可用', invalid_curriculum: '课程结构异常', missing_topic: '知识元信息缺失'
  };
  return meta.warnings.length ? <details><summary>部分注册或课程记录需核查，统计按当前可用记录展示。</summary>
    <ul>{meta.warnings.map(w => <li key={w.code}>{labels[w.code] || '记录需核查'}：{w.count}</li>)}</ul></details> : null;
}
const reasonCopy: Record<Reason, string> = {
  foreground_capture_partial: '已记录前台练习活动用时（非单题、认知投入或完整学习时间）；跨标签页/设备重叠未核实，可能存在未送达尾段。',
  no_recorded_activity: '暂无可用记录，不能据此判断是否学习过。',
  missing_completion_source: '暂无可用完成记录，不能据此判断是否学习过。',
  completion_sources_disagree: '完成记录来源不一致，已保留可核实的完成记录。',
  submission_id_not_captured: '仅统计已保留的尝试记录，可能包含重复提交；不代表完整提交次数。',
  history_incomplete: '历史尝试明细不完整，无法确定完整次数。',
  invalid_attempt_identity: '部分尝试记录无法核实，未计入次数。',
  conflicting_attempt_identity: '部分尝试记录无法核实，未计入次数。',
  duration_provenance_unverified: '历史记录缺少可核实的时长单位或计时范围，暂不显示练习用时。',
  conflicting_explanation_variants: '存在不同版本的记录，当前显示所保留的最新解释。',
  topic_global_private: '独立知识元解释不向教师开放，不计入本课程统计。',
  missing_topic: '知识元信息不可用。', invalid_curriculum: '课程结构异常。',
  empty_population: '无在籍学生。', empty_curriculum: '暂无知识元。', source_limit: '部分记录不可用。'
};
export function Reasons({reasons}: {reasons: Reason[]}) {
  useRecordNotes(reasons.map(reason => reasonCopy[reason]));
  return null;
}
export function MeasureView({measure, unit = ''}: {measure: Measure; unit?: string}) {
  return <><span>{measure.availability === 'not_applicable' ?
    '0 · 不适用' : measure.value === null ?
    measure.reasons.includes('duration_provenance_unverified') ? '— · 待核实' : '— · 暂无数据' : `${measure.value}${unit}`}
    {measure.availability === 'partial' && ' · 部分记录'}</span><Reasons reasons={measure.reasons} /></>;
}
export function ForegroundTimeView({value}: {value?: ForegroundDuration}) {
  const labels: Record<string, string> = {reading: '阅读', video: '视频页面（非播放时长）', exercise: '练习活动',
    quiz: '测验', selfExplanation: '自我解释', survey: '问卷', review: '内容复习', code: '代码与输出',
    demonstration: '演示', aiHelp: 'AI 帮助', chat: '学习对话'};
  const reasons: Record<string, string> = {no_timing_records: '无新版计时记录', overlap_unverified: '跨标签页/设备重叠未核实',
    overlap_excluded: '同一运行时的重叠片段已排除', content_revision_missing: '内容修订标识未记录',
    incomplete_visit: '访问未收到结束记录', sequence_gap: '片段缺失', capture_loss_or_best_effort: '暂停/丢失或卸载尽力送达',
    unsupported_content: '占位复习内容不支持计时', missing_open: '访问起始记录缺失', invalid_timing_visit: '异常计时访问已排除'};
  useRecordNotes((value?.reasons || ['no_timing_records']).map(reason => reasons[reason] || '计时覆盖不完整'));
  return <section aria-label="已记录前台用时"><h3>已记录前台用时</h3>
    <p>{value?.recordedMs == null ? '— · 暂无新版计时' : `${(value.recordedMs / 1000).toFixed(3)} 秒${value.availability === 'partial' ? ' · 部分记录' : ''}`}</p>
    <dl>{Object.entries(value?.bySurface || {}).map(([surface, ms]) =>
      <div key={surface}><dt>{labels[surface]}</dt><dd>{(ms! / 1000).toFixed(3)} 秒</dd></div>)}</dl>
  </section>;
}
export function FractionView({value, unit, compact = false}: {value: Fraction; unit: string; compact?: boolean}) {
  if (!value.total) return <>{compact ? '不适用' : '不适用（暂无知识元）'}</>;
  return <><span>{value.completed.value ?? '—'}/{value.total} {unit}{value.completed.availability === 'partial' && ' · 部分记录'}</span>
    {!compact && value.completed.value === null && <small>暂无可用完成记录</small>}
    {!compact && <small>有完成状态记录 {value.knownItems}/{value.total} · 状态未知 {value.unknownItems}</small>}
    <Reasons reasons={value.completed.reasons} /></>;
}
export function CompletionView({value, compact = false}: {value: Completion; compact?: boolean}) {
  return <><span>{{completed: '已记录完成', not_recorded: '暂无完成记录', unavailable: '完成状态未知', not_applicable: '不适用'}[value.state]}
    {value.availability === 'partial' && ' · 部分记录'}</span>
    {!compact && value.state === 'completed' && <small>完成时间：<DateView value={value.completedAt} /></small>}<Reasons reasons={value.reasons} /></>;
}
export const presenceCopy = (v: boolean | null) => v === null ? '记录状态未知' : v ? '有本课程记录' : '暂无本课程记录';
export function DateView({value, enrollment = false}: {value: string | null; enrollment?: boolean}) {
  return value ? <time dateTime={value}>{enrollment ? new Date(value).toLocaleDateString('zh-CN') :
    new Date(value).toLocaleString('zh-CN', {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})}</time>
    : <>{enrollment ? '注册时间未记录' : '时间未记录'}</>;
}
export function CohortView({value, generatedAt, showFigure = true}: {value: Cohort; generatedAt?: string; showFigure?: boolean}) {
  const metrics = [
    ['已记录完成人数', value.completedStudents, value.completionRate],
    ['已记录练习总次数', value.attempts, undefined],
    ['已记录前台练习用时（秒）', value.exerciseTimeSeconds, undefined],
    ['本课程自我解释参与人数', value.explanationParticipants, value.explanationParticipationRate]
  ] as const;
  return <>
    <p>统计范围：本课程全部在籍学生（{value.enrolledStudents} 人）</p>
    {showFigure && <DataFigure title="学生完成与参与图" scale="percent"
      caption={`全部在籍学生 · ${value.enrolledStudents} 人`}
      rows={[
        {id: 'completion', label: '已记录完成率', value: value.completionRate, availability: value.completedStudents.availability,
          detail: `${value.completedStudents.value ?? '—'}/${value.enrolledStudents} 人 · 有可用统计 ${value.completedStudents.coverage.withEvidence}/${value.completedStudents.coverage.population} 人 · 无可用统计 ${value.completedStudents.coverage.withoutEvidence} 人`},
        {id: 'participation', label: '本课程自我解释参与率', value: value.explanationParticipationRate,
          availability: value.explanationParticipants.availability, accent: 'purple',
          detail: `${value.explanationParticipants.value ?? '—'}/${value.enrolledStudents} 人 · 有可用统计 ${value.explanationParticipants.coverage.withEvidence}/${value.explanationParticipants.coverage.population} 人 · 无可用统计 ${value.explanationParticipants.coverage.withoutEvidence} 人`}
      ]} />}
    <Metrics>{metrics.map(([label, m, r]) => <div key={label}><h3>{label}</h3><MeasureView measure={m} />
      {r !== undefined && <p>{label === '已记录完成人数' ? '已记录完成率' : '参与率'}：{rate(r)} · {m.value ?? '—'}/{value.enrolledStudents} 人{m.availability === 'partial' && ' · 部分记录'}</p>}
      <small>有可用统计 {m.coverage.withEvidence}/{m.coverage.population} 人 · 无可用统计 {m.coverage.withoutEvidence} 人</small>
      {m.coverage.partial > 0 && <small>其中部分记录 {m.coverage.partial} 人</small>}
    </div>)}</Metrics>
    <ForegroundTimeView value={value.foregroundElapsed} />
    {generatedAt && <small>统计生成时间：<DateView value={generatedAt} /></small>}
  </>;
}
export function Placements({topic}: {topic: AnalyticsTopic}) {
  return <><small>{topic.placements.map(p => [p.chapterTitle || '未命名章节', p.sectionId ? p.sectionTitle || '未命名小节' : null].filter(Boolean).join(' / ')).join('；')}</small>
    {topic.placements.length > 1 && <small>多处引用，共用本课程记录</small>}</>;
}
export function useQueryState() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const update = (values: Record<string, string | number | null>, focusId?: string) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => value === null ? next.delete(key) : next.set(key, String(value)));
    setParams(next, {state: {...(location.state || {}), panelFocusId: focusId}});
  };
  const pageNumber = (key = 'page') => Number(params.get(key) || '1');
  return {params, update, pageNumber};
}
export function Pager({pagination: p, onPage, loading = false}: {pagination: Page<unknown>['pagination']; onPage: (page: number) => void; loading?: boolean}) {
  return <nav aria-label="分页"><button disabled={loading || !p.pages || p.page <= 1} onClick={() => onPage(p.page - 1)}>上一页</button>
    <span role="status">{p.pages ? `第 ${p.page} / ${p.pages} 页 · 共 ${p.total} 条` : '共 0 条'}</span>
    <button disabled={loading || !p.pages || p.page >= p.pages} onClick={() => onPage(p.page + 1)}>下一页</button>
    {p.page > 1 && <button disabled={loading} onClick={() => onPage(1)}>返回第一页</button>}</nav>;
}
export function Search({value, onSearch, loading = false}: {value: string; onSearch: (q: string) => void; loading?: boolean}) {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState('');
  useEffect(() => setDraft(value), [value]);
  return <form onSubmit={e => {
    e.preventDefault();
    if (Array.from(draft.trim()).length > 80) { setError('姓名搜索最多输入 80 个字符。'); return; }
    setError(''); onSearch(draft.trim());
  }}><label>按姓名搜索 <input id="analytics-name-search" value={draft} placeholder="输入学生姓名" onChange={e => setDraft(e.target.value)} /></label>
    <button disabled={loading} type="submit">{loading ? '加载中…' : '搜索'}</button>
    <button type="button" disabled={loading || (!draft && !value)} onClick={() => {setDraft(''); setError(''); onSearch('');}}>清除搜索</button>
    {error && <p role="alert">{error}</p>}</form>;
}
export function CourseShell({course, title, children}: {course?: CourseCard; title?: string; children: React.ReactNode}) {
  const location = useLocation();
  const id = course?.courseId || location.pathname.split('/')[3];
  const base = `/teacher/courses/${id}`;
  const tab = new URLSearchParams(location.search).get('tab') || 'overview';
  const savedPage = Number((location.state as {courseListPage?: number} | null)?.courseListPage) || 1;
  const courseListPage = Number.isInteger(savedPage) && savedPage > 0 && savedPage <= 10000 ? savedPage : 1;
  const nav = [
    ['课程概览', `${base}/analytics?tab=overview`, location.pathname.endsWith('/analytics') && tab === 'overview'],
    ['知识元概览', `${base}/analytics?tab=topics&page=1`, location.pathname.endsWith('/analytics') && tab !== 'overview'],
    ['学生列表', `${base}/students?page=1`, location.pathname.includes('/students')]
  ] as const;
  useEffect(() => { const h = document.querySelector('h1'); if (h) { h.tabIndex = -1; h.focus(); } }, [location.pathname]);
  return <DashboardLayout compact title={title || course?.title || (course ? '未命名课程' : '学习记录')}
    subtitle={`${course?.title || ''} · 学习记录 · 当前课程`}>
    <RecordNotesLayout>
    <nav aria-label="课程导航"><Link to={`/teacher/dashboard?view=students&page=${courseListPage}`}>按课程查看学生 / 更换课程</Link>
      <span>{course?.title || '当前课程'}</span><Link to={base}>编辑课程</Link></nav>
    <nav aria-label="学习记录视图">{nav.map(([label, url, active]) => <Link key={label} to={url} state={location.state} aria-current={active ? 'page' : undefined}>{label}</Link>)}</nav>
    {children}
    </RecordNotesLayout>
  </DashboardLayout>;
}
export function StudentIdentity({student}: {student: Student}) {
  return <><strong>{student.name || '未命名学生'}</strong><small>学生标识：{student.studentId}</small></>;
}
const returnPositions = new Map<string, {focusId: string; scrollY: number}>();
export function StudentLink({student, courseId, topicId, children}: {student: Student; courseId: string; topicId?: string; children?: React.ReactNode}) {
  const location = useLocation();
  const id = `student-${student.studentId}${children ? '-name' : ''}`;
  return <Link id={id} to={`/teacher/courses/${courseId}/students/${student.studentId}${topicId ? `?tab=topics&page=1&topicId=${encodeURIComponent(topicId)}` : '?tab=chapters&page=1'}`}
    onClick={() => {
      if (returnPositions.size >= 50) returnPositions.delete(returnPositions.keys().next().value!);
      returnPositions.set(location.pathname + location.search, {focusId: id, scrollY: window.scrollY});
    }}
    state={{...(location.state || {}), returnTo: location.pathname + location.search, focusId: id, scrollY: window.scrollY}}>{children || '查看学习详情'}</Link>;
}
export function safeReturn(courseId: string, value: unknown, topicId?: string) {
  const base = `/teacher/courses/${courseId}`;
  if (typeof value === 'string' && (value.split('?')[0] === `${base}/students` || value.split('?')[0] === `${base}/analytics`) &&
    !value.includes('#') && !value.includes('\\')) return value;
  return topicId ? `${base}/analytics?tab=students&topicId=${encodeURIComponent(topicId)}&page=1` : `${base}/students?page=1`;
}
export function RestoreFocus({ready, onlyRequested = false}: {ready: boolean; onlyRequested?: boolean}) {
  const location = useLocation();
  useEffect(() => {
    if (!ready) return;
    const state = location.state as {focusId?: string; panelFocusId?: string; scrollY?: number} | null;
    if (onlyRequested && !state?.panelFocusId) return;
    const remembered = returnPositions.get(location.pathname + location.search);
    const focusId = state?.panelFocusId || state?.focusId || remembered?.focusId;
    const heading = document.querySelector<HTMLElement>('[data-results-heading]');
    const target = focusId ? document.getElementById(focusId) || heading : heading;
    if (target) {
      for (let parent = target.parentElement; parent; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS' && !parent.querySelector('summary')?.contains(target)) {
          parent.setAttribute('open', '');
        }
      }
      target.focus({preventScroll: true});
    }
    const y = state?.scrollY ?? remembered?.scrollY;
    if (typeof y === 'number' && Number.isFinite(y)) window.scrollTo(0, y);
  }, [ready, onlyRequested, location.key, location.state]);
  return null;
}
